import { useEffect, useMemo, useState } from "preact/hooks"
import { api_fetch, api_fetch_throws } from "@/api"
import { AccountingItemOption, AccountingItemsResponse, listAccountingItems } from "@/api/accounting.tsx"
import { useOrgSettings, loadOrgSettings } from "@/org_settings/state.tsx"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { Info, Trash2 } from "lucide-react"
import { ItemBadge } from "@/accounting/item_badge.tsx"
import { classes } from "@/utils"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Check, ChevronsUpDown } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

export interface LedgerAccountDto {
  id: string
  org_id: string
  account_number: string
  expense_account: string
  hidden_from_deduction_matching: boolean
  is_trade: boolean
  /** The org's one account for distributor admin fees; CodingWiz codes `admin_fee` here. */
  is_admin_fee?: boolean
  usage_notes?: string | null
  product_service?: string | null
  remote_id?: string
}

interface LedgerAccountFormValues {
  account_number: string
  expense_account: string
  usage_notes: string
  product_service: string
}

export type ProductServiceFilter = "all" | "mapped" | "unmapped"

export function matchesProductServiceFilter(acct: LedgerAccountDto, filter: ProductServiceFilter): boolean {
  const mapped = Boolean(acct.product_service?.trim())
  if (filter === "mapped") {
    return mapped
  }
  if (filter === "unmapped") {
    return !mapped
  }
  return true
}

export function sortLedgerAccountsForSettings(accounts: LedgerAccountDto[]): LedgerAccountDto[] {
  return [...accounts].sort(function compare_ledger_accounts(a: LedgerAccountDto, b: LedgerAccountDto) {
    const hidden_order = Number(a.hidden_from_deduction_matching) - Number(b.hidden_from_deduction_matching)
    if (hidden_order !== 0) {
      return hidden_order
    }
    const account_number_order = a.account_number.localeCompare(b.account_number, undefined, { numeric: true, sensitivity: "base" })
    if (account_number_order !== 0) {
      return account_number_order
    }
    return a.expense_account.localeCompare(b.expense_account, undefined, { sensitivity: "base" })
  })
}

export function validateLedgerAccount(values: LedgerAccountFormValues): string | null {
  return !values.account_number.trim() || !values.expense_account.trim()
    ? "Both Account # and Expense Account are required"
    : null
}

export function toLedgerAccountFormValues(acct: LedgerAccountDto): LedgerAccountFormValues {
  return {
    account_number: acct.account_number,
    expense_account: acct.expense_account,
    usage_notes: acct.usage_notes ?? "",
    product_service: acct.product_service ?? "",
  }
}

export function LedgerAccountManageSection() {
  const [accounts, setAccounts] = useState<LedgerAccountDto[]>([])
  const [loading, setLoading] = useState(false)
  const [hidden_filter, set_hidden_filter] = useState<"all" | "shown" | "hidden">("all")
  const [trade_filter, set_trade_filter] = useState<"all" | "trade" | "non_trade">("all")
  const [product_service_filter, set_product_service_filter] = useState<ProductServiceFilter>("all")
  const [selected_ids, set_selected_ids] = useState<Set<string>>(new Set())
  const [bulk_saving, set_bulk_saving] = useState(false)
  const org_settings = useOrgSettings()

  async function load() {
    setLoading(true)
    try {
      const data = await api_fetch_throws<LedgerAccountDto[]>("/reason_codes?include_hidden=true", { use_cache: false })
      setAccounts(sortLedgerAccountsForSettings(data))
    } finally {
      setLoading(false)
    }
  }

  useEffect(function init() {
    load()
    void loadOrgSettings()
  }, [])

  // Product/Service mapping only matters when an accounting integration pushes credit memos
  // (QBO/NetSuite item lines) or the org already has mappings configured.
  const show_product_service =
    org_settings.accounting_integration != null ||
    org_settings.has_product_services ||
    accounts.some((a: LedgerAccountDto) => Boolean(a.product_service?.trim()))
  const mapped_count = accounts.filter((a: LedgerAccountDto) => Boolean(a.product_service?.trim())).length

  function handleCreated(acct: LedgerAccountDto) {
    setAccounts(prev => sortLedgerAccountsForSettings([acct, ...prev]))
  }

  function handleUpdated(acct: LedgerAccountDto) {
    // Only one admin-fee account per org: the server unmarks the others when one is marked.
    setAccounts(prev =>
      sortLedgerAccountsForSettings(
        prev.map(a => (a.id === acct.id ? acct : acct.is_admin_fee && a.is_admin_fee ? { ...a, is_admin_fee: false } : a))
      )
    )
  }

  function handleDeleted(id: string) {
    setAccounts(prev => prev.filter(a => a.id !== id))
    set_selected_ids(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  async function setHiddenMany(ids: string[], next_hidden: boolean) {
    if (ids.length === 0) {
      return
    }
    set_bulk_saving(true)
    const results = await Promise.all(
      ids.map(async function update_one(id: string) {
        const res = await api_fetch<LedgerAccountDto>(`/organization/ledger-accounts/${id}`, {
          method: "PUT",
          body: { hidden_from_deduction_matching: next_hidden },
        })
        return { id, res }
      })
    )
    let ok_count = 0
    const failed_ids: string[] = []
    for (const result of results) {
      if (result.res.ok) {
        ok_count += 1
        handleUpdated(result.res.value.data)
      } else {
        failed_ids.push(result.id)
      }
    }
    set_bulk_saving(false)
    if (ok_count > 0) {
      toast({ title: "Bulk update complete", description: `Updated ${ok_count} account${ok_count === 1 ? "" : "s"}` })
    }
    if (failed_ids.length > 0) {
      toast({
        title: "Some updates failed",
        description: `Could not update ${failed_ids.length} selected row${failed_ids.length === 1 ? "" : "s"}`,
        variant: "destructive",
      })
    }
    set_selected_ids(prev => {
      const next = new Set(prev)
      for (const id of ids) {
        next.delete(id)
      }
      return next
    })
  }

  async function setTradeMany(ids: string[], next_trade: boolean) {
    if (ids.length === 0) {
      return
    }
    set_bulk_saving(true)
    const results = await Promise.all(
      ids.map(async function update_one(id: string) {
        const res = await api_fetch<LedgerAccountDto>(`/organization/ledger-accounts/${id}`, {
          method: "PUT",
          body: { is_trade: next_trade },
        })
        return { id, res }
      })
    )
    let ok_count = 0
    const failed_ids: string[] = []
    for (const result of results) {
      if (result.res.ok) {
        ok_count += 1
        handleUpdated(result.res.value.data)
      } else {
        failed_ids.push(result.id)
      }
    }
    set_bulk_saving(false)
    if (ok_count > 0) {
      toast({ title: "Bulk update complete", description: `Updated ${ok_count} account${ok_count === 1 ? "" : "s"}` })
    }
    if (failed_ids.length > 0) {
      toast({
        title: "Some updates failed",
        description: `Could not update ${failed_ids.length} selected row${failed_ids.length === 1 ? "" : "s"}`,
        variant: "destructive",
      })
    }
    set_selected_ids(prev => {
      const next = new Set(prev)
      for (const id of ids) {
        next.delete(id)
      }
      return next
    })
  }

  const filtered_accounts = accounts.filter(function by_filters(a: LedgerAccountDto) {
    if (hidden_filter === "hidden" && !a.hidden_from_deduction_matching) {
      return false
    }
    if (hidden_filter === "shown" && a.hidden_from_deduction_matching) {
      return false
    }
    if (trade_filter === "trade" && !a.is_trade) {
      return false
    }
    if (trade_filter === "non_trade" && a.is_trade) {
      return false
    }
    if (show_product_service && !matchesProductServiceFilter(a, product_service_filter)) {
      return false
    }
    return true
  })

  const selected_in_view = filtered_accounts.filter(a => selected_ids.has(a.id)).map(a => a.id)

  // Built from the unfiltered list so the add-item dialog knows every mapped item for an account,
  // even when filters hide some sibling rows.
  const mapped_items_by_account = useMemo(
    function build_mapped_items() {
      const map: Record<string, string[]> = {}
      for (const a of accounts) {
        const item = a.product_service?.trim()
        if (item) {
          ;(map[a.account_number] ??= []).push(item)
        }
      }
      return map
    },
    [accounts]
  )

  return (
    <div class="bg-white rounded-lg shadow-sm p-6 space-y-6">
      <h2 class="text-lg font-semibold leading-7 text-gray-900">Ledger Accounts</h2>
      <p class="text-sm text-gray-500">
        Hidden accounts remain in your chart but are excluded from deduction matching pickers and CodingWiz.
      </p>
      <p class="text-sm text-gray-500">
        <strong>Use for</strong> is your "use for the following" guidance for each reason code—describe the kinds of
        charges that belong here (e.g. <em>"Fair Share, Warehouse Fee, EDI charges, admin charges"</em>). These notes
        are shown here and fed to <strong>CodingWiz</strong>, so the AI codes deductions the way your team intends.
      </p>
      <p class="text-sm text-gray-500">
        <strong>Trade</strong> marks whether a reason code counts as trade spend. When enabled as a custom org field,
        this value appears on transactions exports based on each deduction&apos;s coded reason code.
      </p>
      <p class="text-sm text-gray-500">
        <strong>Admin fee</strong> marks the one account for distributor admin fees (e.g. the KeHE or UNFI admin fee on a
        billback). When set, <strong>CodingWiz</strong> codes the fee to it and the rest of the deduction to the account it
        picks.
      </p>
      {show_product_service && (
        <p class="text-sm text-gray-500">
          <strong>Item</strong> is the accounting item used on credit memo lines when pushing to your
          accounting system (e.g. QuickBooks and NetSuite require every credit memo line to reference an active
          item). Items are what actually appear on the published credit memo. A GL account can map to more than
          one item — use <strong>Add item</strong> on a row to add another mapping for the same account.
          Accounts without a mapping will fail to publish —{" "}
          <span class="font-medium text-gray-700">
            {mapped_count} of {accounts.length} accounts mapped
          </span>
          .
        </p>
      )}
      <LedgerAccountForm onCreated={handleCreated} show_product_service={show_product_service} />
      <div class="flex flex-wrap items-center gap-2">
        <Button variant={hidden_filter === "all" ? "default" : "outline"} size="sm" onClick={() => set_hidden_filter("all")}>
          All
        </Button>
        <Button
          variant={hidden_filter === "shown" ? "default" : "outline"}
          size="sm"
          onClick={() => set_hidden_filter("shown")}>
          Shown in matching
        </Button>
        <Button
          variant={hidden_filter === "hidden" ? "default" : "outline"}
          size="sm"
          onClick={() => set_hidden_filter("hidden")}>
          Hidden
        </Button>
        <div class="w-px h-6 bg-gray-200 mx-1" />
        <Button variant={trade_filter === "all" ? "default" : "outline"} size="sm" onClick={() => set_trade_filter("all")}>
          All trade types
        </Button>
        <Button variant={trade_filter === "trade" ? "default" : "outline"} size="sm" onClick={() => set_trade_filter("trade")}>
          Trade
        </Button>
        <Button
          variant={trade_filter === "non_trade" ? "default" : "outline"}
          size="sm"
          onClick={() => set_trade_filter("non_trade")}>
          Non-trade
        </Button>
        {show_product_service && (
          <>
            <div class="w-px h-6 bg-gray-200 mx-1" />
            <Button
              variant={product_service_filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => set_product_service_filter("all")}>
              All mappings
            </Button>
            <Button
              variant={product_service_filter === "mapped" ? "default" : "outline"}
              size="sm"
              onClick={() => set_product_service_filter("mapped")}>
              Mapped
            </Button>
            <Button
              variant={product_service_filter === "unmapped" ? "default" : "outline"}
              size="sm"
              onClick={() => set_product_service_filter("unmapped")}>
              Not mapped
            </Button>
          </>
        )}
        <div class="w-px h-6 bg-gray-200 mx-1" />
        <Button
          variant="outline"
          size="sm"
          disabled={bulk_saving || selected_in_view.length === 0}
          onClick={() => void setHiddenMany(selected_in_view, true)}>
          Hide selected
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={bulk_saving || selected_in_view.length === 0}
          onClick={() => void setHiddenMany(selected_in_view, false)}>
          Show selected
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={bulk_saving || selected_in_view.length === 0}
          onClick={() => void setTradeMany(selected_in_view, true)}>
          Mark selected as trade
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={bulk_saving || selected_in_view.length === 0}
          onClick={() => void setTradeMany(selected_in_view, false)}>
          Mark selected as non-trade
        </Button>
        <span class="text-xs text-gray-500">{bulk_saving ? "Saving…" : `${selected_in_view.length} selected`}</span>
      </div>
      <LedgerAccountsTable
        loading={loading}
        accounts={filtered_accounts}
        show_product_service={show_product_service}
        mapped_items_by_account={mapped_items_by_account}
        selected_ids={selected_ids}
        on_selected_ids_change={(updater: (prev: Set<string>) => Set<string>) => set_selected_ids(updater)}
        onUpdated={handleUpdated}
        onDeleted={handleDeleted}
        onCreated={handleCreated}
      />
    </div>
  )
}

interface LedgerAccountFormProps {
  onCreated: (acct: LedgerAccountDto) => void
  show_product_service: boolean
}

function LedgerAccountForm({ onCreated, show_product_service }: LedgerAccountFormProps) {
  const [values, setValues] = useState<LedgerAccountFormValues>({ account_number: "", expense_account: "", usage_notes: "", product_service: "" })
  const [saving, setSaving] = useState(false)

  function setField<K extends keyof LedgerAccountFormValues>(key: K, value: string) {
    setValues(prev => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: Event) {
    e.preventDefault()
    const err = validateLedgerAccount(values)
    if (err) {
      toast({ title: "Missing fields", description: err, variant: "destructive" })
      return
    }
    setSaving(true)
    const res = await api_fetch<LedgerAccountDto>("/organization/ledger-accounts", {
      method: "POST",
      body: {
        account_number: values.account_number.trim(),
        expense_account: values.expense_account.trim(),
        usage_notes: values.usage_notes.trim() || null,
        product_service: values.product_service.trim() || null,
      },
    })
    if (res.ok) {
      onCreated(res.value.data)
      toast({ title: "Created", description: "Ledger account created" })
      setValues({ account_number: "", expense_account: "", usage_notes: "", product_service: "" })
    } else {
      toast({ title: "Failed to create", description: res.error.message, variant: "destructive" })
    }
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit as any} class="grid grid-cols-1 md:grid-cols-6 gap-4">
      <div class="md:col-span-2">
        <Label>Account #</Label>
        <Input value={values.account_number} onInput={(e: any) => setField("account_number", e.currentTarget.value)} />
      </div>
      <div class={show_product_service ? "md:col-span-2" : "md:col-span-3"}>
        <Label>Expense Account</Label>
        <Input value={values.expense_account} onInput={(e: any) => setField("expense_account", e.currentTarget.value)} />
      </div>
      {show_product_service && (
        <div class="md:col-span-1">
          <Label>Item</Label>
          <Input
            value={values.product_service}
            onInput={(e: any) => setField("product_service", e.currentTarget.value)}
            placeholder="Accounting item"
          />
        </div>
      )}
      <div class="md:col-span-1 flex items-end">
        <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Add"}</Button>
      </div>
      <div class="md:col-span-6">
        <Label>Use for (CodingWiz guidance)</Label>
        <Textarea
          value={values.usage_notes}
          onInput={(e: any) => setField("usage_notes", e.currentTarget.value)}
          // @ts-ignore
          rows={2}
          placeholder="e.g. Fair Share, Warehouse Fee, Repositrack, Wire Fee, EDI charges, admin charge for pulling invoices and deductions, Free Fill"
          class="text-sm"
        />
      </div>
    </form>
  )
}

interface LedgerAccountsTableProps {
  loading: boolean
  accounts: LedgerAccountDto[]
  show_product_service: boolean
  /** Items already mapped per account number (built from the unfiltered account list). */
  mapped_items_by_account: Record<string, string[]>
  selected_ids: Set<string>
  on_selected_ids_change: (updater: (prev: Set<string>) => Set<string>) => void
  onUpdated: (acct: LedgerAccountDto) => void
  onDeleted: (id: string) => void
  onCreated: (acct: LedgerAccountDto) => void
}

function LedgerAccountsTable({ loading, accounts, show_product_service, mapped_items_by_account, selected_ids, on_selected_ids_change, onUpdated, onDeleted, onCreated }: LedgerAccountsTableProps) {
  if (loading) {
    return <div class="text-sm text-gray-500">Loading…</div>
  }
  const all_selected = accounts.length > 0 && accounts.every(a => selected_ids.has(a.id))
  return (
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
              <Checkbox
                checked={all_selected}
                onCheckedChange={(v: boolean | "indeterminate") => {
                  if (v === "indeterminate") {
                    return
                  }
                  on_selected_ids_change(function set_all(prev: Set<string>) {
                    const next = new Set(prev)
                    for (const a of accounts) {
                      if (v) {
                        next.add(a.id)
                      } else {
                        next.delete(a.id)
                      }
                    }
                    return next
                  })
                }}
              />
            </th>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account #</th>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expense Account</th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              title="Plain-language guidance for when to use this code. Shown to your team and fed to CodingWiz.">
              Use for (CodingWiz)
            </th>
            {show_product_service && (
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                title="Accounting item used on credit memo lines pushed to your accounting system. Must match an active item name there.">
                Item
              </th>
            )}
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 tracking-wider w-28 normal-case">
              <span class="inline-flex items-center gap-1">
                Is trade?
                <span class="group relative inline-flex">
                  <Info className="h-3.5 w-3.5 text-gray-400" aria-label="Is trade help" />
                  <span class="pointer-events-none absolute left-0 top-5 z-10 hidden w-44 rounded-md border border-gray-200 bg-white p-2 text-left text-xs font-normal normal-case leading-5 text-gray-600 shadow-lg group-hover:block">
                    Check the box if yes.
                  </span>
                </span>
              </span>
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-gray-500 tracking-wider w-28 normal-case"
              title="The one account CodingWiz codes distributor admin fees to">
              Admin fee
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40"
              title="Hide from deduction matching, pickers, and AI wizards (keeps the code in the system)">
              Hide
            </th>
            <th class="px-3 py-2" />
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          {accounts.map((a: LedgerAccountDto) => (
            <LedgerAccountRow
              key={a.id}
              acct={a}
              show_product_service={show_product_service}
              sibling_items={mapped_items_by_account[a.account_number] ?? []}
              selected={selected_ids.has(a.id)}
              on_select_change={(next_selected: boolean) => {
                on_selected_ids_change(function set_one(prev: Set<string>) {
                  const next = new Set(prev)
                  if (next_selected) {
                    next.add(a.id)
                  } else {
                    next.delete(a.id)
                  }
                  return next
                })
              }}
              onUpdated={onUpdated}
              onDeleted={onDeleted}
              onCreated={onCreated}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface LedgerAccountRowProps {
  acct: LedgerAccountDto
  show_product_service: boolean
  /** Items already mapped on any row of this account number (incl. this row), for the add-item dialog. */
  sibling_items: string[]
  selected: boolean
  on_select_change: (next_selected: boolean) => void
  onUpdated: (acct: LedgerAccountDto) => void
  onDeleted: (id: string) => void
  onCreated: (acct: LedgerAccountDto) => void
}

function LedgerAccountRow({ acct, show_product_service, sibling_items, selected, on_select_change, onUpdated, onDeleted, onCreated }: LedgerAccountRowProps) {
  const [edit_open, set_edit_open] = useState(false)
  const [add_item_open, set_add_item_open] = useState(false)
  const [hiddenSaving, setHiddenSaving] = useState(false)
  const [tradeSaving, setTradeSaving] = useState(false)
  const [adminFeeSaving, setAdminFeeSaving] = useState(false)

  async function setHiddenFromMatching(next: boolean) {
    setHiddenSaving(true)
    const res = await api_fetch<LedgerAccountDto>(`/organization/ledger-accounts/${acct.id}`, {
      method: "PUT",
      body: { hidden_from_deduction_matching: next },
    })
    if (res.ok) {
      onUpdated(res.value.data)
    } else {
      toast({ title: "Could not update", description: res.error.message, variant: "destructive" })
    }
    setHiddenSaving(false)
  }

  async function setIsTrade(next: boolean) {
    setTradeSaving(true)
    const res = await api_fetch<LedgerAccountDto>(`/organization/ledger-accounts/${acct.id}`, {
      method: "PUT",
      body: { is_trade: next },
    })
    if (res.ok) {
      onUpdated(res.value.data)
    } else {
      toast({ title: "Could not update", description: res.error.message, variant: "destructive" })
    }
    setTradeSaving(false)
  }

  async function setIsAdminFee(next: boolean) {
    setAdminFeeSaving(true)
    const res = await api_fetch<LedgerAccountDto>(`/organization/ledger-accounts/${acct.id}`, {
      method: "PUT",
      body: { is_admin_fee: next },
    })
    if (res.ok) {
      onUpdated(res.value.data)
    } else {
      toast({ title: "Could not update", description: res.error.message, variant: "destructive" })
    }
    setAdminFeeSaving(false)
  }

  async function deleteRow() {
    const res = await api_fetch(`/organization/ledger-accounts/${acct.id}`, { method: "DELETE" })
    if (res.ok) {
      onDeleted(acct.id)
      toast({ title: "Deleted", description: "Ledger account deleted" })
    } else {
      toast({ title: "Failed to delete", description: res.error.message, variant: "destructive" })
    }
  }

  return (
    <tr>
      <td class="px-3 py-2 align-middle">
        <Checkbox
          checked={selected}
          onCheckedChange={(v: boolean | "indeterminate") => {
            if (v === "indeterminate") {
              return
            }
            on_select_change(v)
          }}
        />
      </td>
      <td class="px-3 py-2 align-top">
        <span class="text-sm">{acct.account_number}</span>
      </td>
      <td class="px-3 py-2 align-top">
        <span class="text-sm">{acct.expense_account}</span>
      </td>
      <td class="px-3 py-2 align-top max-w-xs">
        {acct.usage_notes ? (
          <span class="text-sm whitespace-pre-wrap text-gray-700">{acct.usage_notes}</span>
        ) : (
          <span class="text-sm text-gray-400 italic">—</span>
        )}
      </td>
      {show_product_service && (
        <td class="px-3 py-2 align-top max-w-xs">
          <div class="flex flex-col items-start gap-1.5">
            {acct.product_service?.trim() ? (
              <ItemBadge item={acct.product_service} />
            ) : (
              <span
                class="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20"
                title="Credit memos coded to this account cannot be pushed until an item is mapped">
                Not mapped
              </span>
            )}
            <button
              type="button"
              class="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
              title="Map another accounting item to this GL account — each item becomes its own coding option and credit memo line"
              onClick={() => set_add_item_open(true)}>
              + Add item
            </button>
            <AddItemDialog
              open={add_item_open}
              onOpenChange={set_add_item_open}
              acct={acct}
              sibling_items={sibling_items}
              onCreated={onCreated}
            />
          </div>
        </td>
      )}
      <td class="px-3 py-2 align-middle">
        <div class="flex items-center gap-2">
          <Checkbox
            checked={acct.is_trade}
            disabled={tradeSaving}
            onCheckedChange={(v: boolean | "indeterminate") => {
              if (v === "indeterminate") {
                return
              }
              void setIsTrade(v)
            }}
          />
          <span class="text-xs text-gray-500">{tradeSaving ? "…" : ""}</span>
        </div>
      </td>
      <td class="px-3 py-2 align-middle">
        <div class="flex items-center gap-2">
          <Checkbox
            checked={Boolean(acct.is_admin_fee)}
            disabled={adminFeeSaving}
            onCheckedChange={(v: boolean | "indeterminate") => {
              if (v === "indeterminate") {
                return
              }
              void setIsAdminFee(v)
            }}
          />
          <span class="text-xs text-gray-500">{adminFeeSaving ? "…" : ""}</span>
        </div>
      </td>
      <td class="px-3 py-2 align-middle">
        <div class="flex items-center gap-2">
          <Checkbox
            checked={acct.hidden_from_deduction_matching}
            disabled={hiddenSaving}
            onCheckedChange={(v: boolean | "indeterminate") => {
              if (v === "indeterminate") {
                return
              }
              void setHiddenFromMatching(v)
            }}
          />
          <span class="text-xs text-gray-500">{hiddenSaving ? "…" : ""}</span>
        </div>
      </td>
      <td class="px-3 py-2 w-40 text-right">
        <div class="flex gap-2 justify-end">
          <Button // @ts-ignore
            size="sm" variant="outline" onClick={() => set_edit_open(true)}>Edit</Button>
          <EditLedgerAccountDialog
            open={edit_open}
            onOpenChange={set_edit_open}
            acct={acct}
            show_product_service={show_product_service}
            onUpdated={onUpdated}
          />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                class={classes("p-1.5 rounded-full transition-colors cursor-pointer hover:bg-red-50")}
                aria-label="Delete ledger account">
                <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete ledger account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. The ledger account will be permanently deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div class="flex justify-end gap-2 mt-4">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteRow as any}>Delete</AlertDialogAction>
              </div>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </td>
    </tr>
  )
}

/**
 * Loads the accounting system's active item list once `enabled` first turns true (the API response
 * is cached, so the add-item dialog and edit dialog share one fetch). `loaded === null` means not
 * loaded yet; an empty `items` list means free-text mode (with `error` set when the load failed).
 */
function useAccountingItems(enabled: boolean): { loaded: AccountingItemsResponse | null; loading: boolean } {
  const [loaded, setLoaded] = useState<AccountingItemsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  useEffect(
    function load_items_when_enabled() {
      if (!enabled || loaded !== null || loading) {
        return
      }
      setLoading(true)
      listAccountingItems()
        .then(res => setLoaded(res.ok ? res.value.data : { items: [], error: res.error.message }))
        .catch(() => setLoaded({ items: [] }))
        .finally(() => setLoading(false))
    },
    [enabled]
  )
  return { loaded, loading }
}

interface AccountingItemComboboxProps {
  value: string
  onChange: (item: string) => void
  options: AccountingItemOption[]
}

/**
 * Searchable picker over the accounting system's active items for the edit dialog — same source,
 * capping, and custom-name fallback as the add-item dialog. Also offers clearing the mapping.
 */
function AccountingItemCombobox({ value, onChange, options }: AccountingItemComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const { shown, total } = useMemo(
    function filter_options() {
      return filter_item_options(options, query)
    },
    [options, query]
  )
  const show_custom = offer_custom_item(query, options)

  function pick(item: string) {
    onChange(item)
    setQuery("")
    // Use setTimeout to prevent the close event from bubbling up to the parent dialog
    setTimeout(() => setOpen(false), 0)
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal">
          {value.trim() ? (
            <span class="truncate">{value}</span>
          ) : (
            <span class="text-gray-500">Select an item…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search active items..." value={query} onValueChange={(v: string) => setQuery(v)} />
          <CommandList className="max-h-60">
            {shown.length === 0 ? <div class="py-6 text-center text-sm text-gray-500">No matching items.</div> : null}
            <CommandGroup>
              {value.trim() && !query.trim() ? (
                <CommandItem key="__clear" value="__clear" onSelect={() => pick("")}>
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  <span class="text-gray-500 italic">No item (clear mapping)</span>
                </CommandItem>
              ) : null}
              {shown.map((option: AccountingItemOption) => {
                const is_current = option.name.trim().toLowerCase() === value.trim().toLowerCase()
                return (
                  <CommandItem key={option.id} value={`${option.name} ${option.id}`} onSelect={() => pick(option.name)}>
                    <Check className={classes("mr-2 h-4 w-4", is_current ? "opacity-100 text-green-600" : "opacity-0")} />
                    <span class="truncate">{option.name}</span>
                    <span class="ml-auto pl-3 text-xs text-gray-400">{option.item_type}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
          {total > shown.length ? (
            <div class="border-t px-4 py-1.5 text-xs text-gray-400">
              Showing {shown.length} of {total} items — keep typing to narrow the list.
            </div>
          ) : null}
          {show_custom ? (
            <button
              type="button"
              class="w-full border-t px-4 py-2.5 text-left text-sm text-indigo-600 hover:bg-indigo-50"
              onClick={() => pick(query.trim())}>
              Use custom name &ldquo;{query.trim()}&rdquo;
            </button>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface EditLedgerAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  acct: LedgerAccountDto
  show_product_service: boolean
  onUpdated: (acct: LedgerAccountDto) => void
}

/** Edits a ledger account in a modal (mirrors the add-item dialog) instead of cramped inline table inputs. */
function EditLedgerAccountDialog({ open, onOpenChange, acct, show_product_service, onUpdated }: EditLedgerAccountDialogProps) {
  const [values, setValues] = useState<LedgerAccountFormValues>(toLedgerAccountFormValues(acct))
  const [saving, setSaving] = useState(false)
  const { loaded: items_loaded, loading: items_loading } = useAccountingItems(open && show_product_service)
  const item_options = items_loaded?.items ?? []

  useEffect(
    function reset_on_open() {
      if (open) {
        setValues(toLedgerAccountFormValues(acct))
      }
    },
    [open, acct]
  )

  function setField<K extends keyof LedgerAccountFormValues>(key: K, value: string) {
    setValues(prev => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: Event) {
    e.preventDefault()
    const err = validateLedgerAccount(values)
    if (err) {
      toast({ title: "Missing fields", description: err, variant: "destructive" })
      return
    }
    setSaving(true)
    const res = await api_fetch<LedgerAccountDto>(`/organization/ledger-accounts/${acct.id}`, {
      method: "PUT",
      body: {
        account_number: values.account_number.trim(),
        expense_account: values.expense_account.trim(),
        usage_notes: values.usage_notes.trim() || null,
        product_service: values.product_service.trim() || null,
      },
    })
    if (res.ok) {
      onUpdated(res.value.data)
      onOpenChange(false)
      toast({ title: "Saved", description: "Ledger account updated" })
    } else {
      toast({ title: "Failed to save", description: res.error.message, variant: "destructive" })
    }
    setSaving(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit ledger account</DialogTitle>
          <DialogDescription>
            {acct.account_number} — {acct.expense_account}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit as any} class="space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <Label>Account #</Label>
              <Input value={values.account_number} onInput={(e: any) => setField("account_number", e.currentTarget.value)} />
            </div>
            <div class="space-y-1.5">
              <Label>Expense Account</Label>
              <Input value={values.expense_account} onInput={(e: any) => setField("expense_account", e.currentTarget.value)} />
            </div>
          </div>
          {show_product_service && (
            <div class="space-y-1.5">
              <Label>Item</Label>
              {items_loading || items_loaded === null ? (
                <div class="text-sm text-gray-500 border rounded-md px-3 py-2">Loading items from your accounting system…</div>
              ) : item_options.length > 0 ? (
                <AccountingItemCombobox
                  value={values.product_service}
                  onChange={(item: string) => setField("product_service", item)}
                  options={item_options}
                />
              ) : (
                <>
                  {items_loaded.error ? (
                    <p class="text-xs text-amber-600">
                      Couldn't load the item list from your accounting system — type the exact active item name instead. ({items_loaded.error})
                    </p>
                  ) : null}
                  <Input
                    value={values.product_service}
                    onInput={(e: any) => setField("product_service", e.currentTarget.value)}
                    placeholder="Active item name in your accounting system"
                  />
                </>
              )}
            </div>
          )}
          <div class="space-y-1.5">
            <Label>Use for (CodingWiz guidance)</Label>
            <Textarea
              value={values.usage_notes}
              onInput={(e: any) => setField("usage_notes", e.currentTarget.value)}
              // @ts-ignore
              rows={4}
              placeholder="When should CodingWiz pick this code? e.g. Fair Share, Warehouse Fee, EDI charges, admin charges"
              class="text-sm"
            />
          </div>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** How the add-item dialog treats one accounting item option. */
export function classify_item_option(name: string, sibling_items: string[]): "mapped" | "available" {
  const norm = name.trim().toLowerCase()
  return sibling_items.some(item => item.trim().toLowerCase() === norm) ? "mapped" : "available"
}

/** Cap on rendered picker rows — thousands of DOM rows made cmdk filtering freeze the dialog. */
export const ITEM_PICKER_LIMIT = 50

/**
 * Case-insensitive substring filter over the item options, capped for rendering. Filtering is done
 * here (not by cmdk) so only the capped list ever reaches the DOM.
 */
export function filter_item_options(
  options: AccountingItemOption[],
  query: string,
  limit: number = ITEM_PICKER_LIMIT
): { shown: AccountingItemOption[]; total: number } {
  const norm = query.trim().toLowerCase()
  const matches = norm ? options.filter((o: AccountingItemOption) => o.name.toLowerCase().includes(norm)) : options
  return { shown: matches.slice(0, limit), total: matches.length }
}

/** Whether the dialog should offer creating `query` as a custom item name (no exact option match). */
export function offer_custom_item(query: string, options: AccountingItemOption[]): boolean {
  const norm = query.trim().toLowerCase()
  if (!norm) {
    return false
  }
  return !options.some(option => option.name.trim().toLowerCase() === norm)
}

interface AddItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  acct: LedgerAccountDto
  /** Items already mapped on any row of this account number — shown as taken, not selectable. */
  sibling_items: string[]
  onCreated: (acct: LedgerAccountDto) => void
}

/**
 * Maps another accounting item to a GL account (a sibling ledger row per item). Items are
 * searched/selected from the accounting system's live active-item list; a custom name can be
 * typed for items the list doesn't have (or when no item-based integration is connected).
 */
function AddItemDialog({ open, onOpenChange, acct, sibling_items, onCreated }: AddItemDialogProps) {
  const [query, setQuery] = useState("")
  const [saving, setSaving] = useState(false)
  const { loaded, loading: loadingOptions } = useAccountingItems(open)
  const options = loaded?.items ?? null

  async function addItemMapping(item: string) {
    if (saving) {
      return
    }
    setSaving(true)
    const res = await api_fetch<LedgerAccountDto>("/organization/ledger-accounts", {
      method: "POST",
      body: {
        account_number: acct.account_number,
        expense_account: acct.expense_account,
        product_service: item,
      },
    })
    if (res.ok) {
      onCreated(res.value.data)
      onOpenChange(false)
      setQuery("")
      toast({ title: "Item added", description: `${acct.account_number} now also maps to "${item}"` })
    } else {
      toast({ title: "Failed to add item", description: res.error.message, variant: "destructive" })
    }
    setSaving(false)
  }

  const has_options = (options?.length ?? 0) > 0
  const { shown, total } = useMemo(
    function filter_options() {
      return filter_item_options(options ?? [], query)
    },
    [options, query]
  )
  const show_custom = offer_custom_item(query, options ?? [])
  const load_error = loaded?.error

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle>Add an item to {acct.account_number}</DialogTitle>
          <DialogDescription>
            {acct.expense_account} — each mapped item is its own coding option and becomes its own credit memo line.
          </DialogDescription>
        </DialogHeader>
        {loadingOptions || (open && loaded === null) ? (
          <div class="px-4 pb-4 text-sm text-gray-500">Loading items from your accounting system…</div>
        ) : has_options ? (
          <Command className="border-t" shouldFilter={false}>
            <CommandInput
              placeholder="Search active items..."
              value={query}
              onValueChange={(v: string) => setQuery(v)}
            />
            <CommandList className="max-h-72">
              {shown.length === 0 ? <div class="py-6 text-center text-sm text-gray-500">No matching items.</div> : null}
              <CommandGroup>
                {shown.map((option: AccountingItemOption) => {
                  const mapped = classify_item_option(option.name, sibling_items) === "mapped"
                  return (
                    <CommandItem
                      key={option.id}
                      value={`${option.name} ${option.id}`}
                      disabled={mapped || saving}
                      onSelect={() => void addItemMapping(option.name)}>
                      <Check className={classes("mr-2 h-4 w-4", mapped ? "opacity-100 text-green-600" : "opacity-0")} />
                      <span class="truncate">{option.name}</span>
                      <span class="ml-auto flex items-center gap-2 pl-3 text-xs text-gray-400">
                        {option.item_type}
                        {mapped ? <span class="text-green-600 font-medium">mapped</span> : null}
                      </span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
            {total > shown.length ? (
              <div class="border-t px-4 py-1.5 text-xs text-gray-400">
                Showing {shown.length} of {total} items — keep typing to narrow the list.
              </div>
            ) : null}
            {show_custom ? (
              <button
                type="button"
                class="w-full border-t px-4 py-2.5 text-left text-sm text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                disabled={saving}
                onClick={() => void addItemMapping(query.trim())}>
                {saving ? "Adding…" : <>Use custom name &ldquo;{query.trim()}&rdquo;</>}
              </button>
            ) : null}
          </Command>
        ) : (
          <div class="border-t px-4 py-4 space-y-2">
            {load_error ? (
              <p class="text-xs text-amber-600">
                Couldn't load the item list from your accounting system — type the exact active item name instead. ({load_error})
              </p>
            ) : null}
            <form
              class="flex items-end gap-2"
              onSubmit={((e: Event) => {
                e.preventDefault()
                if (query.trim()) {
                  void addItemMapping(query.trim())
                }
              }) as any}>
              <div class="flex-1">
                <Label>Item name</Label>
                <Input
                  value={query}
                  onInput={(e: any) => setQuery(e.currentTarget.value)}
                  placeholder="Active item name in your accounting system"
                />
              </div>
              <Button type="submit" disabled={saving || !query.trim()}>
                {saving ? "Adding…" : "Add"}
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// Inline tests
// @vitest-environment jsdom
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest

  it("sortLedgerAccountsForSettings prioritizes shown rows and sorts by account number", () => {
    const sorted = sortLedgerAccountsForSettings([
      {
        id: "3",
        org_id: "org",
        account_number: "100",
        expense_account: "Shown B",
        hidden_from_deduction_matching: false,
        is_trade: false,
      },
      {
        id: "2",
        org_id: "org",
        account_number: "2",
        expense_account: "Hidden A",
        hidden_from_deduction_matching: true,
        is_trade: false,
      },
      {
        id: "1",
        org_id: "org",
        account_number: "20",
        expense_account: "Shown A",
        hidden_from_deduction_matching: false,
        is_trade: true,
      },
    ])
    expect(sorted.map(account => account.id)).toEqual(["1", "3", "2"])
  })

  it("toLedgerAccountFormValues seeds the edit dialog, blanking null fields", () => {
    expect(
      toLedgerAccountFormValues({
        id: "1",
        org_id: "org",
        account_number: "48002",
        expense_account: "Contra Revenue : Slotting",
        hidden_from_deduction_matching: false,
        is_trade: true,
        usage_notes: null,
      })
    ).toEqual({ account_number: "48002", expense_account: "Contra Revenue : Slotting", usage_notes: "", product_service: "" })
  })

  it("validateLedgerAccount returns error when fields empty", () => {
    expect(validateLedgerAccount({ account_number: "", expense_account: "", usage_notes: "", product_service: "" })).toBeTypeOf("string")
  })

  it("validateLedgerAccount passes when fields present", () => {
    expect(validateLedgerAccount({ account_number: "123", expense_account: "4504 : Discounts", usage_notes: "", product_service: "" })).toBeNull()
  })

  it("classify_item_option marks already-mapped items case/whitespace-insensitively", () => {
    const siblings = ["Delivery Chargebacks", " Shortages (Wholesale) "]
    expect(classify_item_option("delivery chargebacks", siblings)).toBe("mapped")
    expect(classify_item_option("Shortages (Wholesale)", siblings)).toBe("mapped")
    expect(classify_item_option("Returns - Wholesale", siblings)).toBe("available")
  })

  it("filter_item_options caps rendering and matches case-insensitive substrings", () => {
    const options = Array.from({ length: 3000 }, (_, i) => ({ id: String(i), name: `1P Athletics Club Tee-${i}` }))
    options.push({ id: "s", name: "Shortages (Wholesale)" })
    const unfiltered = filter_item_options(options, "")
    expect(unfiltered.shown.length).toBe(ITEM_PICKER_LIMIT)
    expect(unfiltered.total).toBe(3001)
    const searched = filter_item_options(options, "  shortages ")
    expect(searched.shown.map(o => o.name)).toEqual(["Shortages (Wholesale)"])
    expect(searched.total).toBe(1)
    expect(filter_item_options(options, "zzz").shown).toEqual([])
  })

  it("offer_custom_item only offers names the option list doesn't already have", () => {
    const options = [{ id: "1", name: "Delivery Chargebacks" }]
    expect(offer_custom_item("", options)).toBe(false)
    expect(offer_custom_item("   ", options)).toBe(false)
    expect(offer_custom_item("delivery chargebacks", options)).toBe(false)
    expect(offer_custom_item("Shortages (Wholesale)", options)).toBe(true)
  })

  it("matchesProductServiceFilter treats blank and missing as unmapped", () => {
    const base = {
      id: "1",
      org_id: "org",
      account_number: "46300",
      expense_account: "Scanbacks & Promos",
      hidden_from_deduction_matching: false,
      is_trade: false,
    }
    const unmapped_missing = base
    const unmapped_blank = { ...base, product_service: "  " }
    const mapped = { ...base, product_service: "BI 2% Allowance" }
    expect(matchesProductServiceFilter(unmapped_missing, "unmapped")).toBe(true)
    expect(matchesProductServiceFilter(unmapped_blank, "unmapped")).toBe(true)
    expect(matchesProductServiceFilter(unmapped_blank, "mapped")).toBe(false)
    expect(matchesProductServiceFilter(mapped, "mapped")).toBe(true)
    expect(matchesProductServiceFilter(mapped, "unmapped")).toBe(false)
    expect(matchesProductServiceFilter(mapped, "all")).toBe(true)
    expect(matchesProductServiceFilter(unmapped_missing, "all")).toBe(true)
  })
}


